/**
 * Tells PowerSync's routine re-authentication apart from a sync token that keeps being refused.
 *
 * The SDK caches the credentials `fetchCredentials` returned and refreshes them shortly before the
 * token expires, on a timer. iOS suspends JavaScript while the app is in the background, so that
 * timer does not run: on resume the stream reconnects with the cached, now-expired token, the
 * service answers 401 `PSYNC_S2103`, and the SDK drops the token and reconnects with a fresh one
 * on its next attempt. One of these is the expected cost of being backgrounded past the token's
 * lifetime, not a failure.
 *
 * The same rejection arriving over and over is a failure: it means every token handed to the SDK
 * is already expired — a device clock far enough off for Clerk to keep serving a dead token, say —
 * and the app will never sync. So only a run of them is reported.
 */

/** PowerSync's code for a JWT whose `exp` has passed. Present in the message the SDK throws. */
const EXPIRED_TOKEN_CODE = 'PSYNC_S2103';

/** A healthy resume costs one rejection; the SDK retries every few seconds when stuck. */
const REJECTIONS_BEFORE_REPORTING = 3;
const REJECTION_WINDOW_MS = 5 * 60_000;

/**
 * Matched on the message because on web the error reaches `statusChanged` from the sync worker,
 * which posts it across as a plain `{ name, message, stack }` — the `status` does not survive.
 */
export const isExpiredSyncTokenError = (error: unknown): error is object =>
  typeof error === 'object' &&
  error !== null &&
  typeof (error as { message?: unknown }).message === 'string' &&
  (error as { message: string }).message.includes(EXPIRED_TOKEN_CODE);

let rejectedAt: number[] = [];

/**
 * One rejection reaches us more than once — the SDK logs it where the request fails, and it then
 * surfaces again as the `downloadError` in `statusChanged` — so each error is counted once and
 * every later look at it gets the same answer.
 */
const verdicts = new WeakMap<object, boolean>();

/**
 * Whether this error is an expired sync token the SDK will recover from by itself, as opposed to
 * one in a run of rejections that a human needs to see.
 */
export const isRoutineSyncTokenExpiry = (error: unknown, now = Date.now()): boolean => {
  if (!isExpiredSyncTokenError(error)) {
    return false;
  }

  const known = verdicts.get(error);
  if (known !== undefined) {
    return known;
  }

  rejectedAt = [...rejectedAt.filter((at) => now - at < REJECTION_WINDOW_MS), now];
  const routine = rejectedAt.length < REJECTIONS_BEFORE_REPORTING;
  verdicts.set(error, routine);

  return routine;
};

/** For tests: forget earlier rejections. */
export const resetSyncTokenExpiryTracking = () => {
  rejectedAt = [];
};
